'use client';

import { useCallback, useState } from 'react';
import type { PrimaryNavItem } from '../types';
import { DEFAULT_GROUP_ID } from '../config';

interface UseNavigationProps {
  navigation: PrimaryNavItem[];
  defaultGroupId?: number;
  onNavigationChange?: (groupId: number) => void;
}

interface UseNavigationReturn {
  primaryIndex: number;
  secondaryIndex: number;
  currentGroupId: number;
  /** 选中一级导航 */
  selectPrimary: (index: number) => void;
  /** 选中二级导航 */
  selectSecondary: (index: number) => void;
  /** PC 端：点击已选中的二级导航取消选中 */
  toggleSecondary: (primaryIndex: number, secondaryIndex: number) => void;
  /** 当前一级导航数据 */
  currentPrimary: PrimaryNavItem;
  /** 当前一级导航下的二级导航列表 */
  currentSecondaryNav: PrimaryNavItem['secondaryNav'];
  /** PC 端选中的二级导航索引（按一级索引） */
  pcSelectedSecondary: Record<number, number | null>;
}

export function getDefaultPcSecondaryLocation(navigation: PrimaryNavItem[]) {
  for (let pIndex = 0; pIndex < navigation.length; pIndex += 1) {
    const sIndex = navigation[pIndex]?.secondaryNav.findIndex((item) => item.isDefault);
    if (sIndex != null && sIndex >= 0) {
      return { primaryIndex: pIndex, secondaryIndex: sIndex };
    }
  }

  for (let pIndex = 0; pIndex < navigation.length; pIndex += 1) {
    const sIndex = navigation[pIndex]?.secondaryNav.findIndex((item) => !item.hidden);
    if (sIndex != null && sIndex >= 0) {
      return { primaryIndex: pIndex, secondaryIndex: sIndex };
    }
  }

  return null;
}

function createPcSelectedSecondary(
  navigation: PrimaryNavItem[],
  selected?: { primaryIndex: number; secondaryIndex: number } | null,
): Record<number, number | null> {
  const next = navigation.reduce<Record<number, number | null>>((acc, _, index) => {
    acc[index] = null;
    return acc;
  }, {});

  if (selected) {
    next[selected.primaryIndex] = selected.secondaryIndex;
  }

  return next;
}

export function useNavigation({
  navigation,
  defaultGroupId,
  onNavigationChange,
}: UseNavigationProps): UseNavigationReturn {
  const initialPcDefault = getDefaultPcSecondaryLocation(navigation);
  const [primaryIndex, setPrimaryIndex] = useState(initialPcDefault?.primaryIndex ?? 0);

  // PC 端每个一级导航维护独立的二级选中状态
  const [pcSelectedSecondary, setPcSelectedSecondary] = useState<Record<number, number | null>>(
    () => createPcSelectedSecondary(navigation, initialPcDefault),
  );

  // M 端的二级选中状态
  const [mobileSecondaryIndex, setMobileSecondaryIndex] = useState<number | null>(null);

  const currentPrimary = navigation[primaryIndex] || navigation[0];
  const currentSecondaryNav = currentPrimary?.secondaryNav || [];

  // 获取当前 group_id
  const getCurrentGroupId = useCallback(
    (pIndex: number, sIndex: number | null): number => {
      const primary = navigation[pIndex];
      if (!primary) return defaultGroupId || DEFAULT_GROUP_ID;

      if (sIndex === null) {
        // 选中默认（ALL）
        const defaultNav = primary.secondaryNav.find((s) => s.isDefault);
        return defaultNav?.groupId || defaultGroupId || DEFAULT_GROUP_ID;
      }

      const secondary = primary.secondaryNav[sIndex];
      return secondary?.groupId || defaultGroupId || DEFAULT_GROUP_ID;
    },
    [navigation, defaultGroupId],
  );

  const currentGroupId = getCurrentGroupId(
    primaryIndex,
    mobileSecondaryIndex ?? pcSelectedSecondary[primaryIndex] ?? null,
  );

  const selectPrimary = useCallback(
    (index: number) => {
      if (index < 0 || index >= navigation.length) return;
      setPrimaryIndex(index);
      // M 端重置为第一个二级导航
      setMobileSecondaryIndex(0);
      const groupId = getCurrentGroupId(index, 0);
      onNavigationChange?.(groupId);
    },
    [navigation, getCurrentGroupId, onNavigationChange],
  );

  const selectSecondary = useCallback(
    (index: number) => {
      setMobileSecondaryIndex(index);
      const groupId = getCurrentGroupId(primaryIndex, index);
      onNavigationChange?.(groupId);
    },
    [primaryIndex, getCurrentGroupId, onNavigationChange],
  );

  const toggleSecondary = useCallback(
    (pIndex: number, sIndex: number) => {
      // 同步切换一级导航，确保 currentGroupId 等逻辑基于正确的一级导航
      setPrimaryIndex(pIndex);
      setPcSelectedSecondary((prev) => {
        const currentSelected = prev[pIndex];
        // 再次点击取消选中，回到全量 PC 二级导航中的默认项；没有默认项则回到第一个二级导航
        if (currentSelected === sIndex) {
          const defaultLocation = getDefaultPcSecondaryLocation(navigation);
          if (!defaultLocation) return createPcSelectedSecondary(navigation);

          setPrimaryIndex(defaultLocation.primaryIndex);
          const groupId = getCurrentGroupId(
            defaultLocation.primaryIndex,
            defaultLocation.secondaryIndex,
          );
          onNavigationChange?.(groupId);
          return createPcSelectedSecondary(navigation, defaultLocation);
        }
        // 选中新的二级，清空其他一级的选中
        const groupId = getCurrentGroupId(pIndex, sIndex);
        onNavigationChange?.(groupId);
        return createPcSelectedSecondary(navigation, {
          primaryIndex: pIndex,
          secondaryIndex: sIndex,
        });
      });
    },
    [navigation, getCurrentGroupId, onNavigationChange],
  );

  // 当前一级导航下的 PC 选中状态
  const currentPcSecondaryIndex = pcSelectedSecondary[primaryIndex];

  return {
    primaryIndex,
    secondaryIndex: mobileSecondaryIndex ?? currentPcSecondaryIndex ?? -1,
    currentGroupId,
    selectPrimary,
    selectSecondary,
    toggleSecondary,
    currentPrimary,
    currentSecondaryNav,
    pcSelectedSecondary,
  };
}
